// BL598 Phase 1A + BL600/BL608 (Option A) — `grid pickup <entity-name-or-id> [target-dir]`.
//
// `pickup` is the canonical takeover verb in the 3-verb collaboration model. It
// does two things, in order:
//
//   1. Records the grant/claim server-side by firing
//      `POST /api/v2/entities/:id/pickup`. The server AUTO-DETECTS the target
//      (BL600 + BL608): a regular grid entity records a COLLABORATOR grant (so a
//      later `plug` of that grid's entity is authorized); an anon-minted
//      Guest-Grid drop transfers OWNERSHIP to the caller's grid. The CLI does
//      NOT branch on target type — the server picks the path.
//   2. Lays down the working tree locally (download source + write
//      `cloudgrid.yaml` + `.cloudgrid/link.json`), exactly as before, so the
//      next `grid plug` deploys against the right entity.
//
// Before Option A, step 1 was skipped — pickup was a local clone only and never
// told the server, so a later `plug` of another grid's entity failed with
// FORBIDDEN_ROLE (the collaborator grant was never recorded). Firing `/pickup`
// first closes that gap.
//
// `--no-bind` skips step 2 (records the grant but lays down nothing);
// `--claim-token` passes the claim link for an anon Guest-Grid drop picked up
// from a transport with no anon-session cookie.

import { Command as CommanderCommand } from 'commander';
import { getV2Client, type IV2Client } from '../api/v2-client.js';
import { handleV2Error } from '../handle-error.js';
import { V2ApiError } from '../api/v2-errors.js';
import { notSignedInMessage } from '../active-org.js';
import { CloudGridError } from '../utils.js';
import { runClone } from './clone.js';
import { addGridOption, gridSlug, type GridOpts } from '../grid-opt.js';
import { Output } from '../output.js';
import { addOutputOption, isStructuredOutput } from '../services/output-format.js';

/**
 * Response of `POST /api/v2/entities/:target/pickup` — the unified §4 pickup
 * contract (pickup-for-replug). One shape for every path; the CLI derives the
 * human message from `owner.is_you` + `capabilities.replug`. Unknown extra
 * fields are forward-compatible additions.
 */
interface PickupResponse {
  entity_id: string;
  /** The entity slug (was `name` pre-unification). */
  slug: string;
  grid: string;
  kind?: string;
  /** Canonical public serving URL. Always present. */
  url: string;
  owner: { handle: string | null; is_you: boolean };
  capabilities: { replug: boolean; fork: boolean; reason?: string };
}

export interface RunPickupOpts {
  client: IV2Client;
  /** Entity slug / id / `grid/slug` to pick up. */
  target: string;
  /** Optional directory to lay the working tree into. Defaults to the name. */
  targetDir?: string;
  cwd?: string;
  /** `--org <slug>` — active-grid override for a bare-slug target. */
  org?: string;
  /** Lay the working tree into a non-empty directory. */
  force?: boolean;
  /** ADR 036 V3 — pick up an older version's source + yaml instead of HEAD. */
  version?: string;
  /** Record the grant only — skip the local working-tree download. */
  noBind?: boolean;
  /** Claim link for an anon Guest-Grid drop (cookie-less transports). */
  claimToken?: string;
  /** Emit the raw pickup contract as JSON (alias of `-o json`). */
  json?: boolean;
  /** Raw `-o` / `--output` value (json | yaml | table | wide). */
  output?: string;
}

export async function runPickup(opts: RunPickupOpts): Promise<void> {
  const target = (opts.target ?? '').trim();
  if (target.length === 0) {
    throw new CloudGridError('Provide an entity name or id: grid pickup <name>');
  }

  // Scope the client to `--org` when given, so a bare-slug target resolves in
  // the intended grid (the pickup route reads the active-grid header for a bare
  // slug; a UUID / `grid/slug` resolves globally regardless).
  const client = opts.org !== undefined ? opts.client.withOrgSlug(opts.org) : opts.client;

  // ── Step 1: record the grant/claim server-side ──────────────────────────
  const body: { claim_token?: string } = {};
  if (opts.claimToken) body.claim_token = opts.claimToken;

  let picked: PickupResponse;
  try {
    picked = await client.post<PickupResponse>(
      `/api/v2/entities/${encodeURIComponent(target)}/pickup`,
      body,
    );
  } catch (err) {
    if (err instanceof V2ApiError) throw mapPickupError(err, target);
    throw err;
  }

  // JSON/YAML mode is a read/introspection call: emit the raw pickup contract
  // verbatim and stop. No human chatter, and NO working-tree clone (binding
  // stays available via the default non-JSON path).
  const out = Output.fromOpts({ json: !!opts.json, quiet: false, output: opts.output });
  if (out.isStructured) {
    out.json(() => picked);
    return;
  }

  // Human message derived from the §4 contract (owner vs collaborator vs
  // not-owner), not the retired `role_granted`/`name` fields.
  const where = picked.grid ? ` in ${picked.grid}` : '';
  if (picked.owner.is_you) {
    // The caller owns it (a fresh owned pickup, or a claimed anon drop now theirs).
    process.stdout.write(`Picked up ${picked.slug}${where} — it's yours. Your next plug updates it.\n`);
    if (picked.url) process.stdout.write(`  Public URL: ${picked.url}\n`);
  } else if (picked.capabilities.replug) {
    // Write access without ownership: a collaborator grant on an existing entity.
    process.stdout.write(`Picked up ${picked.slug}${where}. Your next plug updates it.\n`);
  } else {
    // Viewable but not yours to edit — offer the fork path.
    process.stdout.write(`You can view ${picked.slug}${where}, but it's not yours to edit. Fork it to change your own copy.\n`);
  }

  // ── Step 2: lay down the working tree locally ───────────────────────────
  // Address the clone by the server-confirmed entity_id so the local fetch
  // targets the exact entity the grant/claim was recorded against — no second
  // resolution, no ambiguity if the target was a bare slug.
  await runClone({
    client,
    target: picked.entity_id,
    targetDir: opts.targetDir,
    cwd: opts.cwd,
    org: opts.org,
    force: opts.force,
    version: opts.version,
    noBind: opts.noBind,
  });
}

/** Map pickup-endpoint errors to plain, actionable messages (§23 voice). */
function mapPickupError(err: V2ApiError, target: string): CloudGridError {
  if (
    err.code === 'AUTH_REQUIRED' ||
    err.code === 'AUTH_EXPIRED' ||
    err.code === 'AUTH_INVALID' ||
    err.code === 'SIGN_UP_REQUIRED'
  ) {
    return new CloudGridError(notSignedInMessage());
  }
  if (err.code === 'NOT_A_GRID_MEMBER') {
    // The server's message already names the grid to ask into; surface it.
    return new CloudGridError(
      err.message || `You're not a member of the grid that owns '${target}'. Ask its owner to invite you first.`,
    );
  }
  if (err.code === 'FORBIDDEN_ROLE') {
    return new CloudGridError(
      err.message || `Picking up '${target}' needs builder or admin role in its grid.`,
    );
  }
  if (err.code === 'CLAIM_TOKEN_REQUIRED') {
    return new CloudGridError(
      err.message ||
        `'${target}' is an anonymous drop — pass its claim link with --claim-token, or open it from the browser that made the drop.`,
    );
  }
  if (err.code === 'INVALID_CLAIM_TOKEN') {
    return new CloudGridError(err.message || 'That claim link is invalid or has expired.');
  }
  if (err.code === 'ALREADY_CLAIMED') {
    return new CloudGridError(
      `'${target}' was just claimed by someone else. Nothing to pick up.`,
    );
  }
  if (err.code === 'NOT_A_RUNTIME') {
    return new CloudGridError(
      `'${target}' is an Inspiration, not an app or agent. Pick up a runtime, or fork the inspiration instead.`,
    );
  }
  if (err.status === 404 || err.code.endsWith('_NOT_FOUND') || err.code === 'NOT_FOUND') {
    return new CloudGridError(`No entity matched '${target}'. Check the id or grid/slug.`);
  }
  if (err.code === 'RATE_LIMITED') {
    return new CloudGridError('Too many pickups today. Try again later.');
  }
  if (err.code === 'BAD_REQUEST') {
    return new CloudGridError(
      err.message ||
        `Couldn't resolve '${target}'. Pass a full id or grid/slug, or set an active grid with --grid.`,
    );
  }
  return new CloudGridError(
    `Server error (${err.status} ${err.code}). Try again, or run grid feedback if it persists.`,
  );
}

/**
 * Build the pickup verb (and its `pull` alias) from one definition so the two
 * can never drift. `pull` is a decided alias of `pickup` (mega-plan v2 §1, §6,
 * and founder Decision A — "reuse `pull` as a `pickup` alias: YES"). Both verbs
 * run the identical flow: record the grant/claim server-side, then download
 * source + cloudgrid.yaml and link the folder. For an owner pulling their own
 * app this is the §8 "Continue" path — the server records no event, and the
 * builder ends up with their source locally plus a linked folder ready to
 * `grid plug`. The only per-verb difference is the name and description.
 */
function makePickupLikeCommand(verb: string, description: string): CommanderCommand {
  const cmd = new CommanderCommand(verb)
    .description(description)
    .argument('<name>', 'Entity slug, id, or grid/slug to pick up')
    .argument('[target-dir]', 'Directory to pick up into (defaults to the entity name)');
  addGridOption(cmd, 'Grid to resolve the entity in (defaults to the active grid)');
  addOutputOption(cmd);
  return cmd
    .option('--json', 'Emit the raw pickup contract as JSON (alias of -o json); skips the working-tree clone')
    .option('--force', 'Pick up into a non-empty directory')
    .option('--version <id>', "Pick up an older version's source + cloudgrid.yaml instead of HEAD")
    .option('--claim-token <token>', 'Claim link for an anonymous drop (when picking up a Guest-Grid drop)')
    .option('--no-bind', 'Record the grant only — skip the source download, cloudgrid.yaml, and the link')
    .action(
      async (
        name: string,
        targetDir: string | undefined,
        opts: GridOpts & {
          force?: boolean; bind?: boolean; version?: string; claimToken?: string;
          json?: boolean; output?: string;
        },
      ) => {
        try {
          const grid = gridSlug(opts);
          const client = await getV2Client(grid);
          await runPickup({
            client,
            target: name,
            targetDir,
            org: grid,
            force: opts.force,
            version: opts.version,
            claimToken: opts.claimToken,
            // Commander maps `--no-bind` to `opts.bind === false`.
            noBind: opts.bind === false,
            json: opts.json,
            output: opts.output,
          });
        } catch (err) {
          await handleV2Error(err, { json: isStructuredOutput(opts) });
        }
      },
    );
}

export const pickupCommand: CommanderCommand = makePickupLikeCommand(
  'pickup',
  'Adopt an entity: record the grant/claim server-side, then download its source + cloudgrid.yaml and link the folder to it.',
);

/**
 * `grid pull <ref>` — the owner-facing alias of `pickup` (mega-plan v2 §1, §6,
 * Decision A). Get your app's source locally and keep going: it downloads the
 * source + cloudgrid.yaml and links the folder so your next `grid plug` updates
 * the same entity. Contrast `grid download`, which fetches source bytes only —
 * no cloudgrid.yaml, no link.
 */
export const pullCommand: CommanderCommand = makePickupLikeCommand(
  'pull',
  "Continue an entity you can access: download its source + cloudgrid.yaml and link the folder so plug updates it (alias of pickup).",
);
